import Masonry from 'masonry-layout';
import Offcanvas from 'smg/offcanvas';
import { G } from 'smg/querystring';

import * as luxon from 'luxon';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import { CleanNumber } from 'smg/number';
import * as phone from 'smg/phone';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { GetFirstAndLast } from 'smg/users';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

if (document.body.id === 'gallery') {
    main();
}

function main() {
    const masonry = new Masonry('#gallery-images', {
        itemSelector: '.gallery-image-parent:not(.d-none)',
    });

    const $filterSections = document.querySelectorAll<HTMLElement>('.filter-section');
    const $filterCheckboxes = document.querySelectorAll<HTMLInputElement>('.filter-checkbox');

    const $imgParents = document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent');
    const imageDetails = new Map<HTMLElement, Map<string, string>>();
    for (const $imageParent of Array.from($imgParents)) {
        const $img = $imageParent.querySelector('img') as HTMLImageElement;
        const detailsJSON = $img.getAttribute('data-details') || '';
        const details = JSON.parse(detailsJSON) as Record<string, string>;

        const detailsMap = new Map<string, string>();
        for (const [key, value] of Object.entries(details)) {
            detailsMap.set(key, value);
        }

        imageDetails.set($imageParent, detailsMap);
    }

    const compareImageToFilters = (image: HTMLElement, filters: Map<string, Set<string>>) => {
        const details = imageDetails.get(image);
        if (!details) {
            return false;
        }

        let show = true;
        for (const [filterName, filterValue] of filters) {
            if (!details.has(filterName) || !filterValue.has(details.get(filterName) as string)) {
                show = false;
                break;
            }
        }
        return show;
    };

    const getFilteredImages = (filters: Map<string, Set<string>>) => {
        const filteredImages = new Set<HTMLElement>();
        imageDetails.forEach((details, $image) => {
            if (compareImageToFilters($image, filters)) {
                filteredImages.add($image);
            }
        });
        return filteredImages;
    };

    const checkedFilters: () => Map<string, Set<string>> = () => {
        const filters = new Map<string, Set<string>>();
        $filterCheckboxes.forEach($checkbox => {
            if ($checkbox.checked) {
                const filter = String($checkbox.getAttribute('data-filter') || '');
                const value = $checkbox.value;

                if (!filters.get(filter)) {
                    filters.set(filter, new Set([value]));
                } else {
                    filters.get(filter)?.add(value);
                }
            }
        });

        return filters;
    };

    const toggleImages = () => {
        const filters = checkedFilters();

        $imgParents.forEach($ => $.classList.remove('d-none'));

        if (filters.size === 0) {
            $filterCheckboxes.forEach($checkbox => {
                $checkbox.disabled = false;
                $checkbox.parentElement?.classList.remove('d-none');
            });

            $filterSections.forEach($section => {
                $section.classList.remove('d-none');
                const $checkedCountBadge = $section.querySelector('.filter-checked-count') as HTMLElement;
                if ($checkedCountBadge) {
                    $checkedCountBadge.innerText = '0';
                    $checkedCountBadge.classList.add('d-none');
                }
            });

            setTimeout(() => {
                masonry.reloadItems?.();
                masonry.layout?.();
            });
            return;
        }

        const filteredImages = getFilteredImages(filters);
        imageDetails.forEach((details, $image) => {
            if (!filteredImages.has($image)) {
                $image.classList.add('d-none');
            }
        });

        $filterCheckboxes.forEach($checkbox => {
            $checkbox.disabled = false;
            $checkbox.parentElement?.classList.remove('d-none');

            if ($checkbox.checked) {
                return;
            }

            const filter = $checkbox.getAttribute('data-filter') || '';
            const value = $checkbox.value;

            const filtersIfChecked = new Map<string, Set<string>>();
            for (const [filterName, filterValue] of filters) {
                filtersIfChecked.set(filterName, new Set(filterValue));
            }
            if (!filtersIfChecked.has(filter)) {
                filtersIfChecked.set(filter, new Set());
            }
            filtersIfChecked.get(filter)?.add(value);
            const filteredImagesIfChecked = getFilteredImages(filtersIfChecked);

            let filterResultsSame = true;
            if (filteredImagesIfChecked.size !== filteredImages.size) {
                filterResultsSame = false;
            }
            for (const $image of filteredImages) {
                if (!filteredImagesIfChecked.has($image)) {
                    filterResultsSame = false;
                    break;
                }
            }

            if (filteredImagesIfChecked.size === 0 || filterResultsSame) {
                $checkbox.disabled = true;
                $checkbox.parentElement?.classList.add('d-none');
            }
        });

        $filterSections.forEach($section => {
            const $checkboxes = $section.querySelectorAll<HTMLInputElement>('.filter-checkbox');
            if (Array.from($checkboxes).every($checkbox => $checkbox.disabled)) {
                $section.classList.add('d-none');
            } else {
                $section.classList.remove('d-none');
            }
            const checkedCount = Array.from($checkboxes).filter($checkbox => $checkbox.checked).length;
            const $checkedCountBadge = $section.querySelector('.filter-checked-count') as HTMLElement;
            if ($checkedCountBadge) {
                $checkedCountBadge.innerText = checkedCount.toString();
                $checkedCountBadge.classList.toggle('d-none', checkedCount === 0);
            }
        });

        setTimeout(() => {
            masonry.reloadItems?.();
            masonry.layout?.();
        });
    };

    let filtersQueryString = G().Get('filters');
    if (typeof filtersQueryString === 'undefined') {
        if (window.location.host.includes('challengecoins')) {
            filtersQueryString = 'lineItem=Challenge Coins';
        } else if (window.location.host.includes('pins')) {
            filtersQueryString = 'lineItem=Lapel Pins';
        } else if (window.location.host.includes('patches')) {
            filtersQueryString = 'lineItem=Patches';
        } else if (window.location.host.includes('lanyards')) {
            filtersQueryString = 'lineItem=Lanyards';
        }
    }
    if (filtersQueryString) {
        const filters: Record<string, string[]> = filtersQueryString.split('|').reduce((acc: Record<string, string[]>, filter) => {
            const [key, value] = filter.split('=');
            acc[key] = value.split('_');
            return acc;
        }, {});

        Object.entries(filters).forEach(([filter, values]) => {
            values.forEach(value => {
                const $checkbox = document.querySelector<HTMLInputElement>(`input[data-filter="${CSS.escape(filter)}"][value="${CSS.escape(value)}"]`);
                if ($checkbox) {
                    $checkbox.checked = true;
                }
            });
        });
    }

    const setQueryString = () => {
        const filters = checkedFilters();
        // values joined by `_`, filters joined by `|`
        G().Set('filters', Array.from(filters).map(([filter, values]) => `${filter}=${Array.from(values).join('_')}`).join('|'));
    };

    $filterCheckboxes.forEach($checkbox => {
        $checkbox.addEventListener('change', () => {
            setQueryString();
            toggleImages();
        });
    });

    const $filtersParent = document.querySelector('#gallery-filters-parent') as HTMLElement;
    const $filters = document.querySelector('#gallery-filters') as HTMLElement;

    const $showFilters = document.querySelector<HTMLButtonElement>('#gallery-show-filters');
    $showFilters?.addEventListener('click', () => {
        new Offcanvas({
            title: 'Filters',
            body: $filters,
            cancelText: 'Close',
            appendClassBySelector: {
                '.offcanvas-body': 'p-0',
            },
            onClose() {
                $filtersParent.appendChild($filters);
            },
        });
    });

    const $spinner = document.querySelector('#gallery-spinner') as HTMLElement;
    $spinner.remove();

    const $galleryContainer = document.querySelector('#gallery-container') as HTMLElement;
    $galleryContainer.classList.remove('d-none');

    toggleImages();

    const $galleryQuoteTemplate = document.querySelector('#gallery-quote-template') as HTMLTemplateElement;

    const $galleryLinks = document.querySelectorAll<HTMLAnchorElement>('.gallery-image-parent');
    for (const $linkParent of Array.from($galleryLinks)) {
        const $link = $linkParent.querySelector('a') as HTMLAnchorElement;
        $link.addEventListener('click', e => {
            e.preventDefault();

            let details: Map<string, string> | undefined;
            const detailsAttr = $link.querySelector('img')?.getAttribute('data-details') || '';
            if (detailsAttr) {
                const detailsJSON = JSON.parse(detailsAttr) as Record<string, string>;
                details = new Map<string, string>();
                for (const [key, value] of Object.entries(detailsJSON)) {
                    details.set(key, value);
                }
            }

            if (!details) {
                return;
            }

            const $img = $link.querySelector('img') as HTMLImageElement;

            const $body = template.Clone($galleryQuoteTemplate).firstElementChild as HTMLElement;

            const $imgParent = $body.querySelector('#modal-img-parent') as HTMLElement;
            $imgParent.innerHTML = `
                <picture>
                    <source srcset="${html.Escape($link.getAttribute('data-srcset-webp') ?? '')}" type="image/webp">
                    <img src="${html.Escape($link.href)}" class="d-block w-100 h-auto" style="max-width:600px"
                        alt="${html.Escape($img.title)}"
                        title="${html.Escape($img.title)}"
                        data-details="${html.Escape($img.getAttribute('data-details') ?? '')}"
                        srcset="${html.Escape($link.getAttribute('srcset') ?? '')}" width="${$img.width}" height="${$img.height}">
                </picture>`;

            const title = $body.querySelector('.quote-title') as HTMLInputElement;
            let product = 'quote';
            if (details?.has('lineItem')) {
                switch (details.get('lineItem')) {
                case 'Challenge Coins':
                    product = 'coin';
                    break;
                case 'Lapel Pins':
                    product = 'pin';
                    break;
                case 'Patches':
                    product = 'patch';
                    break;
                case 'Lanyards':
                    product = 'lanyard';
                    break;
                case 'Belt Buckles':
                    product = 'buckle';
                    break;
                case 'Dog Tags':
                    product = 'dog tag';
                    break;
                case 'Keychains':
                    product = 'keychain';
                    break;
                case 'Medals':
                    product = 'medal';
                    break;
                default:
                    product = details.get('lineItem') || 'quote';
                    break;
                }
                title.textContent = `Start a ${product} like this!`;
            }

            const $name = $body.querySelector('#modal-full-name') as HTMLInputElement;
            const $email = $body.querySelector('#modal-email') as HTMLInputElement;

            const $phone = $body.querySelector('#modal-phone') as HTMLInputElement;
            const p = new phone.Phone($phone);

            const $quantity = $body.querySelector('#modal-quantity') as HTMLInputElement;

            const $dateNeeded = $body.querySelector('#modal-date-needed') as HTMLInputElement;

            const $notes = $body.querySelector('#modal-notes') as HTMLTextAreaElement;

            const $sms = $body.querySelector('#modal-sms') as HTMLInputElement;

            const upload = $body.querySelector('#modal-upload') as HTMLLabelElement;
            let u: uploads.Uploader;
            setTimeout(() => {
                u = new uploads.Uploader(upload, {
                    confirmBtn: m.Element.querySelector('button[type="submit"]') as HTMLButtonElement,
                });
            });

            let invoiceNumber = details.get('invoiceNumber') || '';
            if (invoiceNumber) {
                invoiceNumber = ` (order #${invoiceNumber})`;
            }

            const m = new Modal({
                // title: $link.dataset.title,
                body: $body,
                preSubmit: () => {
                    $dateNeeded.setCustomValidity('');
                    if ($dateNeeded.value && luxon.DateTime.fromISO($dateNeeded.value) < luxon.DateTime.now()) {
                        $dateNeeded.setCustomValidity('Please enter a date in the future.');
                    }
                },
                onSubmit: () => quoterequests.NewQuoteRequest({
                    company: '',
                    name: $name.value,
                    email: $email.value,
                    phone: p,
                    quantity: parseInt(CleanNumber($quantity.value), 10),
                    sms: $sms.checked,
                    lineItem: details.get('lineItem'),
                    form: 'main gallery',
                    notes: `I would like something like this ${product}${invoiceNumber} from your main gallery: ${$img.src}\n\n${$notes.value}`,
                    dateNeeded: $dateNeeded.value,
                    uploader: u,
                }).then(async quoteRequest => {
                    const sess = await Session().catch(() => undefined);

                    const dataLayer = ((window.top as myWindow) || window).dataLayer || [];
                    const { firstName, lastName } = GetFirstAndLast(String($name.value));
                    dataLayer.push({
                        'event': 'hugo-usa-coins-quote-request-form-success',
                        'userData': {
                            'email': sess?.email || $email.value,
                            'phone': sess?.phone || await p.Format(),
                            'firstName': sess?.firstName || firstName,
                            'lastName': sess?.lastName || lastName,
                        },
                        'transactionId': quoteRequest.id,
                        'total': quoteRequest.quotes?.[0].cachedTotal,
                    });

                    m.Close();

                    const $body = html.FromString(`<div>
                                <p>
                                    We received your quote request! You should hear back from us within one business day.
                                </p>

                                <h3>Here's what you requested:</h3>

                                <ul></ul>

                                </div>`);

                    const $ul = $body.querySelector('ul') as HTMLElement;

                    if (quoteRequest.firstName || quoteRequest.lastName) {
                        $ul.appendChild(html.FromString(`<li><strong>Name:</strong> ${html.Escape(`${quoteRequest.firstName ?? ''} ${quoteRequest.lastName ?? ''}`)}</li>`));
                    }

                    if (quoteRequest.company) {
                        $ul.appendChild(html.FromString(`<li><strong>Company:</strong> ${html.Escape(quoteRequest.company)}</li>`));
                    }

                    if (quoteRequest.email) {
                        $ul.appendChild(html.FromString(`<li><strong>Email:</strong> ${html.Escape(quoteRequest.email)}</li>`));
                    }

                    if (quoteRequest.phone) {
                        $ul.appendChild(html.FromString(`<li><strong>Phone:</strong> ${html.Escape(quoteRequest.phone)}</li>`));
                    }

                    $ul.appendChild(html.FromString('<li><strong>Product:</strong> Challenge Coins</li>'));
                    // get type value from select option text

                    if (quoteRequest.quantity) {
                        $ul.appendChild(html.FromString(`<li><strong>Quantity:</strong> ${Intl.NumberFormat().format(quoteRequest.quantity)}</li>`));
                    }

                    if (quoteRequest.dateNeeded) {
                        $ul.appendChild(html.FromString(`<li><strong>Date Needed:</strong> ${html.Escape(luxon.DateTime.fromISO(quoteRequest.dateNeeded).toLocaleString(luxon.DateTime.DATE_FULL))}</li>`));
                    }

                    if ($notes.value) {
                        $ul.appendChild(html.FromString(`<li><strong>Notes:</strong> ${html.Escape($notes.value)}</li>`));
                    }

                    const successModal = new Modal({
                        title: 'Success!',
                        body: $body,
                        cancelText: 'Close',
                        buttons: [{
                            class: 'btn btn-primary',
                            text: 'Request another quote',
                            click: () => {
                                successModal.Close();
                                $link.click();
                            },
                        }],
                    });
                }),
                size: 'modal-xl',
                cancelText: 'Close',
            });

            // const $gallery = document.querySelector('.gallery') as HTMLElement;
            // const $galleryItem = $gallery.querySelector('.gallery-item') as HTMLElement;
            // $galleryItem.innerHTML = `<img src="${$img.src}" alt="${$img.alt}" class="img-fluid">`;
            // $gallery.classList.add('show');

            return false;
        });
    }
}
